import type { SocialMediaPostData } from "@contracts/shareStop";
import type { LocationHighlightsPostFilter } from "@contracts/socialReports";
import dayjs from "dayjs";

/** Keep these rules aligned with LocationReferenceMatcher and LocationHighlightsReportBuilder. */
export function isLocationPostPublished(post: SocialMediaPostData): boolean {
  return post.status === "Published" || post.postedOn != null;
}

export function getLocationPostDate(post: SocialMediaPostData): string | null {
  return post.postedOn ?? post.scheduledToPostAt?.postAtUtc ?? null;
}

function postTimestamp(post: SocialMediaPostData): number {
  const date = getLocationPostDate(post);
  return date ? Date.parse(date) : Number.NEGATIVE_INFINITY;
}

function isApprovedOrPublished(post: SocialMediaPostData): boolean {
  return post.status === "Approved" || isLocationPostPublished(post);
}

export function filterLocationPosts(posts: SocialMediaPostData[], postFilter: LocationHighlightsPostFilter, startDate?: string | null, endDate?: string | null): SocialMediaPostData[] {
  const start = startDate ? Date.parse(`${startDate}T00:00:00Z`) : null;
  const endExclusive = endDate ? Date.parse(`${endDate}T00:00:00Z`) + 86400000 : null;
  return posts
    .filter(post => {
      if (post.deletedDate != null) return false;
      if (postFilter === "OnlyApproved" && !isApprovedOrPublished(post)) return false;
      if (postFilter === "OnlyPublished" && !isLocationPostPublished(post)) return false;
      if (!getLocationPostDate(post)) return start === null && endExclusive === null;
      const date = postTimestamp(post);
      return (start === null || date >= start) && (endExclusive === null || date < endExclusive);
    })
    .sort((a, b) => {
      const aDate = postTimestamp(a);
      const bDate = postTimestamp(b);
      if (aDate !== bDate) return aDate > bDate ? -1 : 1;
      return (b.createdDate ?? "").localeCompare(a.createdDate ?? "") || a.id.localeCompare(b.id);
    });
}

/** Approved/published posts take precedence over drafts, matching the report's Last highlighted field. */
export function getLastHighlightedPost(posts: SocialMediaPostData[]): SocialMediaPostData | null {
  let last: SocialMediaPostData | null = null;
  for (const post of posts) {
    if (!getLocationPostDate(post)) continue;
    const preferred = isApprovedOrPublished(post);
    const lastPreferred = last ? isApprovedOrPublished(last) : false;
    if (!last || (preferred && !lastPreferred) || (preferred === lastPreferred && postTimestamp(post) > postTimestamp(last))) last = post;
  }
  return last;
}

/** A compact description of the posts already selected by the caller's filters. Each post counts once. */
export function buildLocationHighlightSummary(posts: SocialMediaPostData[], now = new Date()): { headline: string; recency: string } {
  const countLabel = `${posts.length.toLocaleString("en-US")} ${posts.length === 1 ? "highlight" : "highlights"}`;
  const today = dayjs(now);
  const dated = posts.filter(post => {
    const date = getLocationPostDate(post);
    return date != null && dayjs(date).isValid();
  });
  const past = dated.filter(post => !dayjs(getLocationPostDate(post)).isAfter(today));
  const upcoming = dated.filter(post => dayjs(getLocationPostDate(post)).isAfter(today));
  let headline = countLabel;

  // Undated drafts count too, but can't truthfully be assigned to a historical time window.
  if (dated.length && dated.length === posts.length) {
    const dates = dated.map(post => dayjs(getLocationPostDate(post)));
    const oldest = dates.reduce((a, b) => (a.isBefore(b) ? a : b));
    const end = dates.reduce((a, b) => (a.isAfter(b) ? a : b), today);
    const days = [30, 60, 90].find(days => !oldest.isBefore(end.subtract(days, "day")));
    let period: string;
    if (days) period = `${days} days`;
    else if (!oldest.isBefore(end.subtract(6, "month"))) period = "6 months";
    else {
      const years = Math.max(1, Math.ceil(end.diff(oldest, "year", true)));
      period = years === 1 ? "year" : `${years} years`;
    }
    headline += upcoming.length ? ` in the last ${period === "year" ? "a year" : period}` : ` in the last ${period}`;
  }

  const last = getLastHighlightedPost(past);
  if (last) return { headline, recency: `Last highlighted ${relativeHighlightDate(getLocationPostDate(last) ?? "", now)}` };
  if (upcoming.length) {
    const next = upcoming.reduce((a, b) => (postTimestamp(a) < postTimestamp(b) ? a : b));
    return { headline, recency: `Next highlight ${relativeHighlightDate(getLocationPostDate(next) ?? "", now)}` };
  }
  return { headline, recency: posts.length ? "No highlight date yet" : "Not yet highlighted" };
}

function relativeHighlightDate(date: string, now: Date): string {
  const target = dayjs(date);
  const today = dayjs(now);
  const days = target.startOf("day").diff(today.startOf("day"), "day");
  const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(days) < 60) return relative.format(days, "day");
  const months = target.diff(today, "month");
  if (Math.abs(months) < 12) return relative.format(months || Math.sign(days), "month");
  return relative.format(target.diff(today, "year"), "year");
}
